import React from 'react';
import {AnimatePresence} from '@sail/engine';
import {
  // eslint-disable-next-line no-restricted-imports
  Dialog as SailDialog,
  DialogContentConfig,
  tokens,
  useTokenValue,
  css,
} from '@sail/ui';
import {createPortal} from 'react-dom';
import {uniqueId} from 'lodash-es';
import type {OverlayContentProps} from './Overlay';
import {MIN_WIDTH} from '../../utils/Responsive/breakpoints';
import {OverlayContext} from '../../context/overlay/OverlayContext';
import {useLayerContext} from '../../context/layer/LayerContext';

type OverlayContentStackProps = {
  props: Omit<OverlayContentProps, 'children'>;
  ref: HTMLDivElement | null;
  setRef: (ref: HTMLDivElement | null) => void;
  id: string;
};

const DialogStackContext = React.createContext<{
  stack: OverlayContentStackProps[];
  overlayHidden?: boolean;
  insertOrUpdateProps: (props: OverlayContentStackProps) => void;
  remove: (id: string) => void;
} | null>(null);

const StyledDialog = ({
  overlayHidden,
  handleClose,
  size,
  dataTestId,
  hasWhiteBackground,
  allowFullDialogHeight,
  confirmCloseMessages,
  children,
}: {
  overlayHidden?: boolean;
  children?: React.ReactNode;
  handleClose: () => void;
  size?: 'small' | 'medium' | 'large';
  dataTestId?: string;
  hasWhiteBackground?: boolean;
  allowFullDialogHeight?: boolean;
  confirmCloseMessages?: {
    title: string;
    description: string;
    cancelAction: string;
    exitAction: string;
  };
}) => {
  const whiteBackground = useTokenValue(tokens.color.neutral[0]);

  const maxHeightOverride = [
    css({
      ['@media (min-width: 768px)' as '@media SAIL-3040']: {
        maxHeight: '100vh !important',
      },
    }),
  ];

  return (
    <SailDialog
      modality={(overlayHidden ? 'none' : 'modal') as any}
      onClose={handleClose}
      size={size}
      css={{
        ...(hasWhiteBackground ? {backgroundColor: whiteBackground} : {}),
        maxWidth: '100vw', // This allows the Dialog to go fullscreen when the window width is smaller than the width set on the drawer from the size prop
        minWidth: `${MIN_WIDTH}px`,
        visibility: overlayHidden ? 'hidden' : 'visible',
      }}
      uses={[
        ...(allowFullDialogHeight ? maxHeightOverride : []),
        DialogContentConfig.customize({
          css: {
            stack: 'y',
            padding: 'space.0',
          },
        }),
      ]}
      data-testid={dataTestId}
      confirmCloseMessages={confirmCloseMessages}
    >
      {children}
    </SailDialog>
  );
};

export const DialogStackProvider: React.FC = ({children}) => {
  const [stack, setStack] = React.useState<OverlayContentStackProps[]>([]);

  const insertOrUpdateProps = React.useCallback(
    (props: OverlayContentStackProps) => {
      setStack((prev) => {
        const index = prev.findIndex((item) => item.id === props.id);

        if (index === -1) {
          return [...prev, props];
        }

        const newStack = [...prev];
        newStack[index] = props;
        return newStack;
      });
    },
    [],
  );

  const remove = React.useCallback((id: string) => {
    setStack((prev) => prev.filter((item) => item.id !== id));
  }, []);

  const {overlayHidden} = React.useContext(OverlayContext) || {
    overlayHidden: false,
  };

  const value = React.useMemo(
    () => ({stack, insertOrUpdateProps, remove, overlayHidden}),
    [stack, insertOrUpdateProps, remove, overlayHidden],
  );

  return (
    <DialogStackContext.Provider value={value}>
      {children}
    </DialogStackContext.Provider>
  );
};

const useDialogStack = () => {
  const context = React.useContext(DialogStackContext);
  if (!context) {
    throw Error('Dialog stack context is not defined');
  }
  return context;
};

const InnerDialog: React.FC<OverlayContentProps> = (props): JSX.Element => {
  const {
    children,
    handleClose,
    size,
    dataTestId,
    hasWhiteBackground,
    allowFullDialogHeight,
    confirmCloseMessages,
  } = props;
  const [id] = React.useState(() => uniqueId());
  const [ref, setRef] = React.useState<HTMLDivElement | null>(null);
  const {stack, overlayHidden, insertOrUpdateProps, remove} = useDialogStack();

  React.useEffect(() => {
    insertOrUpdateProps({
      props: {
        handleClose,
        size,
        dataTestId,
        hasWhiteBackground,
        allowFullDialogHeight,
        confirmCloseMessages,
      },
      ref,
      setRef,
      id,
    });
  }, [
    handleClose,
    size,
    dataTestId,
    hasWhiteBackground,
    allowFullDialogHeight,
    id,
    insertOrUpdateProps,
    ref,
    confirmCloseMessages,
  ]);

  React.useEffect(() => () => remove(id), [id, remove]);

  const top = stack[stack.length - 1];

  return (
    <>
      {ref ? createPortal(children, ref) : <></>}
      {stack.length > 0 && stack[0].id === id && (
        <AnimatePresence>
          {top && (
            <StyledDialog {...top.props} overlayHidden={overlayHidden}>
              {stack.map((content: any) => (
                <div
                  data-testid="dialog-stack-content"
                  style={{
                    display: top.id === content.id ? 'block' : 'none',
                  }}
                  key={content.id}
                  ref={(ref) => ref && content.setRef(ref)}
                />
              ))}
            </StyledDialog>
          )}
        </AnimatePresence>
      )}
    </>
  );
};

const GuaranteedDialogStackProvider: React.FC = ({children}) => {
  const context = React.useContext(DialogStackContext);
  if (context) {
    return <>{children}</>;
  }
  return <DialogStackProvider>{children}</DialogStackProvider>;
};

export const Dialog: React.FC<OverlayContentProps> = (props): JSX.Element => {
  const layerContext = useLayerContext();

  if (layerContext?.disableEmbeddedLayering) {
    return <StyledDialog {...props} />;
  }

  return (
    <GuaranteedDialogStackProvider>
      <InnerDialog {...props} />
    </GuaranteedDialogStackProvider>
  );
};
